import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

const FORM_URL = "https://readdy.ai/api/form/d7528t75hic0eqh31dtg";

export default function ContactSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const serviceOptions = [
    { key: "contact_svc1", value: "Accounting Services" },
    { key: "contact_svc2", value: "Taxation Services" },
    { key: "contact_svc3", value: "MPF Services" },
    { key: "contact_svc4", value: "Business Advisory" },
    { key: "contact_svc5", value: "General Enquiry" },
  ];

  const contactDetails = [
    { icon: "ri-map-pin-line", labelKey: "contact_d1_label", valueKey: "contact_d1_value" },
    { icon: "ri-phone-line", labelKey: "contact_d2_label", valueKey: "contact_d2_value" },
    { icon: "ri-mail-line", labelKey: "contact_d3_label", valueKey: "contact_d3_value" },
    { icon: "ri-time-line", labelKey: "contact_d4_label", valueKey: "contact_d4_value" },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = data.get("message") as string;
    if (message && message.length > 500) return;

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("sending");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="text-[#D4927A] text-sm font-semibold tracking-widest uppercase">{t("contact_tag")}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C2331] mt-2">{t("contact_heading")}</h2>
          <p className="text-[#6B7280] mt-3 max-w-lg mx-auto text-sm leading-relaxed">
            {t("contact_desc")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact info */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="rounded-2xl overflow-hidden h-48">
              <img
                src="https://public.readdy.ai/ai/img_res/941be1fa2eedb865d22eec88a8537126.jpg"
                alt="Nice Accounting Office"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {contactDetails.map((d) => (
              <div key={d.labelKey} className="flex items-start gap-4 bg-white border border-gray-100 rounded-xl px-4 py-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#FAE8DF] flex-shrink-0">
                  <i className={`${d.icon} text-[#D4927A] text-lg`} />
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">{t(d.labelKey)}</p>
                  <p className="text-sm text-[#1C2331] font-semibold mt-0.5 leading-snug">{t(d.valueKey)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-3 bg-white border border-gray-100 rounded-2xl p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mb-4">
                  <i className="ri-checkbox-circle-fill text-green-500 text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#1C2331] mb-2">{t("contact_success_title")}</h3>
                <p className="text-[#6B7280] text-sm max-w-xs">{t("contact_success_desc")}</p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-6 text-[#D4927A] text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
                >
                  {t("contact_success_again")}
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-[#1C2331] mb-1.5">{t("contact_f_name")}</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder={t("contact_f_name_ph")}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1C2331] placeholder-gray-300 focus:outline-none focus:border-[#D4927A] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1C2331] mb-1.5">{t("contact_f_email")}</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder={t("contact_f_email_ph")}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1C2331] placeholder-gray-300 focus:outline-none focus:border-[#D4927A] transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-[#1C2331] mb-1.5">{t("contact_f_phone")}</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={t("contact_f_phone_ph")}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1C2331] placeholder-gray-300 focus:outline-none focus:border-[#D4927A] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1C2331] mb-1.5">{t("contact_f_service")}</label>
                    <select
                      name="service"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1C2331] focus:outline-none focus:border-[#D4927A] transition-colors bg-white cursor-pointer"
                    >
                      <option value="">{t("contact_f_service_ph")}</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{t(opt.key)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1C2331] mb-1.5">{t("contact_f_company")}</label>
                  <input
                    type="text"
                    name="company"
                    placeholder={t("contact_f_company_ph")}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1C2331] placeholder-gray-300 focus:outline-none focus:border-[#D4927A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1C2331] mb-1.5">
                    {t("contact_f_msg")}
                    <span className={`ml-2 font-normal ${charCount > 500 ? "text-red-500" : "text-gray-400"}`}>
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder={t("contact_f_msg_ph")}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1C2331] placeholder-gray-300 focus:outline-none focus:border-[#D4927A] transition-colors resize-none"
                  />
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-xs">{t("contact_error")}</p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending" || charCount > 500}
                  className="w-full flex items-center justify-center gap-2 bg-[#D4927A] hover:bg-[#C07F6A] disabled:opacity-50 text-white font-semibold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  {status === "sending" ? (
                    <><i className="ri-loader-4-line animate-spin" /> {t("contact_f_sending")}</>
                  ) : (
                    <><i className="ri-send-plane-line" /> {t("contact_f_submit")}</>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400">
                  {t("contact_f_privacy")}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
